/**
 * NaamDarbar — API Client
 *
 * Axios-based API client with interceptors for:
 *  - Automatic Firebase auth token attachment (LAZY — never loads the
 *    Firebase SDK for anonymous visitors)
 *  - Error handling / logging
 */

import axios from 'axios';
import { wasAuthed, getFirebaseAuth } from './firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach Firebase ID token if user is signed in.
// IMPORTANT: We only call getFirebaseAuth() when there's a real chance of an
// active session (`wasAuthed()` flag set in a previous browser session). For
// the common anonymous path this short-circuits and Firebase never loads.
//
// We MUST NOT overwrite an Authorization header the caller already set (e.g.
// the admin JWT injected by useAdmin().adminHeaders()). Without this guard,
// a logged-in admin who is also Firebase-signed-in would have their admin
// token silently replaced by the Firebase token mid-session as soon as
// Firebase finished loading, causing 401s on later admin requests.
api.interceptors.request.use(async (config) => {
  if (!wasAuthed()) return config;
  if (config.headers?.Authorization) return config;
  try {
    const { auth } = await getFirebaseAuth();
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // Firebase load or token refresh failed — continue without auth
  }
  return config;
});

// Admin 401 handler — registered by AdminContext. When an admin API call comes
// back 401 (session expired / revoked server-side), we clear the local admin
// session and bounce to the login screen instead of leaving a dead UI up.
// Kept as a module-level callback so the single shared `api` instance can reach
// React state without importing the context (which would create a cycle).
let onAdminUnauthorized: (() => void) | null = null;
export function setAdminUnauthorizedHandler(fn: (() => void) | null) {
  onAdminUnauthorized = fn;
}

// Response interceptor for error logging (dev-only to avoid leaking info in production)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (import.meta.env.DEV) {
      console.error('[API Error]', error.response?.status, error.response?.data);
    }
    // Session-expiry logout for admin routes only. Exclude /admin/auth/* so a
    // bad-credentials 401 on login isn't mistaken for an expired session.
    const url: string = error.config?.url ?? '';
    const isAdminApi = url.includes('/admin/') && !url.includes('/admin/auth/');
    if (error.response?.status === 401 && isAdminApi) {
      onAdminUnauthorized?.();
    }
    return Promise.reject(error);
  },
);
